import React, { useCallback, useState } from 'react';
import { Circle, Group, Line } from 'react-konva';
import { SocketDefinition, ThemePalette, KonvaMouseEvent } from '../../types';

interface SocketComponentProps {
  socket: SocketDefinition;
  nodeId: string;
  isConnected: boolean;
  isConnecting: boolean;
  accentColor: string;
  onSocketMouseDown: (nodeId: string, socketId: string) => void;
  onSocketMouseUp: (nodeId: string, socketId: string) => void;
  onSocketDoubleClick?: (nodeId: string, socketId: string) => void;
  onSocketSlideStart?: (nodeId: string, socketId: string) => void;
  palette: ThemePalette;
  /** Drawing shapes: no input/output color until this socket is on an edge. */
  neutralUntilConnected?: boolean;
  /** When connected, color by edge role (source=purple, target=green) not leftover socket.type. */
  connectedAs?: 'source' | 'target' | null;
}

const INPUT_COLOR = '#10b981'; // emerald
const OUTPUT_COLOR = '#818cf8'; // indigo-300

export const SocketComponent: React.FC<SocketComponentProps> = ({
  socket,
  nodeId,
  isConnected,
  isConnecting,
  accentColor,
  onSocketMouseDown,
  onSocketMouseUp,
  onSocketDoubleClick,
  onSocketSlideStart,
  palette,
  neutralUntilConnected = false,
  connectedAs = null,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const polarityVisible = !neutralUntilConnected || isConnected;
  const isInput = connectedAs === 'target' ? true : connectedAs === 'source' ? false : socket.type === 'input';
  const idleColor = palette.socketIdle;
  const baseColor = polarityVisible ? (isInput ? INPUT_COLOR : OUTPUT_COLOR) : idleColor;
  const hoverColor = polarityVisible ? (isInput ? '#34d399' : '#a5b4fc') : '#94a3b8';

  const fillColor = isHovered ? hoverColor : isConnected ? baseColor : idleColor;
  const strokeColor = isHovered || isConnected ? baseColor : palette.socketStroke;
  const radius = isHovered ? 7 : 5.5;
  const glowRadius = isHovered ? 14 : 0;

  const handleMouseDown = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.stopPropagation();
    // Alt+drag = slide socket along shape boundary
    if (e.evt.altKey && onSocketSlideStart) {
      onSocketSlideStart(nodeId, socket.id);
      return;
    }
    onSocketMouseDown(nodeId, socket.id);
  }, [nodeId, socket.id, onSocketMouseDown, onSocketSlideStart]);

  const handleMouseUp = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.stopPropagation();
    onSocketMouseUp(nodeId, socket.id);
  }, [nodeId, socket.id, onSocketMouseUp]);

  const handleDoubleClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.stopPropagation();
    onSocketDoubleClick?.(nodeId, socket.id);
  }, [nodeId, socket.id, onSocketDoubleClick]);

  const handleMouseEnter = useCallback((e: KonvaMouseEvent) => {
    setIsHovered(true);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = onSocketSlideStart ? 'grab' : 'crosshair';
  }, [onSocketSlideStart]);

  const handleMouseLeave = useCallback((e: KonvaMouseEvent) => {
    setIsHovered(false);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = isConnecting ? 'crosshair' : 'default';
  }, [isConnecting]);

  // Direction tick mark (small line indicating input/output)
  const tickLen = 8;
  let tickPoints: number[];
  if (socket.tickSide === 'up' || socket.tickSide === 'down') {
    const dir = socket.tickSide === 'up' ? -1 : 1;
    tickPoints = [socket.position.x, socket.position.y + dir * 2, socket.position.x, socket.position.y + dir * tickLen];
  } else {
    const isTickLeft = socket.tickSide ? socket.tickSide === 'left' : isInput;
    const tickX = isTickLeft ? socket.position.x - tickLen : socket.position.x + tickLen;
    tickPoints = isTickLeft
      ? [socket.position.x - 2, socket.position.y, tickX, socket.position.y]
      : [socket.position.x + 2, socket.position.y, tickX, socket.position.y];
  }

  return (
    <Group>
      {/* Glow ring when hovered */}
      {isHovered && (
        <Circle
          x={socket.position.x}
          y={socket.position.y}
          radius={glowRadius}
          fill={`${baseColor}22`}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}

      {/* Connection tick */}
      <Line
        points={tickPoints}
        stroke={isConnected || isHovered ? baseColor : palette.socketIdle}
        strokeWidth={1.5}
        listening={false}
        perfectDrawEnabled={false}
        opacity={isConnected || isHovered ? 1 : 0.5}
      />

      {/* Socket circle */}
      <Circle
        x={socket.position.x}
        y={socket.position.y}
        radius={radius}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={1.5}
        shadowColor={isHovered ? baseColor : accentColor}
        shadowBlur={isHovered ? 10 : isConnected ? 4 : 0}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onDblClick={handleDoubleClick}
        onDblTap={handleDoubleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        perfectDrawEnabled={false}
        hitStrokeWidth={10}
      />
    </Group>
  );
};
